import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X } from "lucide-react";

// LocalStorage = "never show again" (after explicit Install or Dismiss)
const DISMISS_KEY = "pbo-install-dismissed";
// SessionStorage = "shown this session, don't show again until next visit"
const SESSION_SHOWN_KEY = "pbo-install-shown";
const SHOW_DELAY_MS = 1800;
const AUTO_HIDE_MS = 10000;

const LOGO =
  "https://customer-assets.emergentagent.com/job_fairway-picks/artifacts/hbhwifnu_ChatGPT%20Image%20Jun%208%2C%202026%2C%2001_54_26%20PM.png";

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const installEvtRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    // Already permanently dismissed?
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return;
      if (sessionStorage.getItem(SESSION_SHOWN_KEY) === "1") return;
    } catch {
      // privacy mode – just continue
    }

    // Already running as a PWA?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const ua = navigator.userAgent || navigator.vendor || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iOS Safari path: there is no programmatic prompt — show instructions.
    if (isIOS) {
      const t = setTimeout(() => showOnce(true), SHOW_DELAY_MS);
      return () => clearTimeout(t);
    }

    // Chrome / Edge / Android path: capture the install event.
    const handler = (e) => {
      e.preventDefault();
      installEvtRef.current = e;
      showOnce(false);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // If the user installs while the prompt is open, dismiss it.
    const onInstalled = () => dismiss(true);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showOnce = (iosFlag) => {
    setIosMode(iosFlag);
    setVisible(true);
    try {
      sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
    } catch {
      // ignore storage errors
    }
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
  };

  const dismiss = (persist) => {
    setVisible(false);
    clearTimeout(hideTimerRef.current);
    if (persist) {
      try {
        localStorage.setItem(DISMISS_KEY, "1");
      } catch {
        // ignore storage errors
      }
    }
  };

  const handleInstall = async () => {
    const evt = installEvtRef.current;
    if (!evt) return;
    try {
      evt.prompt();
      const { outcome } = await evt.userChoice;
      if (outcome === "accepted") {
        dismiss(true);
      } else {
        dismiss(false);
      }
    } catch {
      dismiss(false);
    } finally {
      installEvtRef.current = null;
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-40 fade-in"
      data-testid="install-prompt"
      role="dialog"
      aria-live="polite"
    >
      <div className="relative bg-pbo-green text-pbo-cream rounded-2xl shadow-2xl border border-pbo-gold/40 p-4 flex gap-3">
        <img
          src={LOGO}
          alt=""
          className="w-12 h-12 rounded-xl bg-pbo-cream/10 p-1 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-pbo-gold-soft">
            Install app
          </div>
          <h3 className="font-serif text-lg leading-tight mt-0.5">
            Add Palm Breezy Open to your home screen
          </h3>

          {iosMode ? (
            <div className="text-xs text-pbo-cream/85 mt-2 leading-relaxed">
              Tap the{" "}
              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-pbo-cream/15 align-middle">
                <Share className="w-3 h-3" /> Share
              </span>{" "}
              icon below, then choose{" "}
              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-pbo-cream/15 align-middle">
                <Plus className="w-3 h-3" /> Add to Home Screen
              </span>
              .
            </div>
          ) : (
            <>
              <p className="text-xs text-pbo-cream/85 mt-1">
                One-tap access for draft night — no more digging through Safari.
              </p>
              <Button
                onClick={handleInstall}
                className="mt-3 h-9 bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold text-xs rounded-full px-4"
                data-testid="install-prompt-install-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Install
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => dismiss(true)}
          aria-label="Dismiss install prompt"
          className="absolute top-2 right-2 text-pbo-cream/60 hover:text-pbo-cream p-1 rounded"
          data-testid="install-prompt-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
